<script setup lang="ts">
import { nextTick, onBeforeUnmount, onActivated, onDeactivated, ref, watch } from 'vue';
import { useAppStore } from '@/stores/app';

defineProps<{
  /** Accessible name of the dialog */
  label: string;
}>();

const open = defineModel<boolean>('open', { required: true });

// A pull this far down dismisses the sheet, whatever the speed.
const DISMISS_DISTANCE = 80;
// Matches the panel's CSS transition: the root element declares none, so Vue cannot detect it.
const TRANSITION_DURATION = 250;

const appStore = useAppStore();
const dragOffset = ref(0);
const isDragging = ref(false);
const panel = ref<HTMLElement>();
let dragStartY = 0;
let opener: HTMLElement | null = null;

function close() {
  open.value = false;
}

function onTouchStart(event: TouchEvent) {
  dragStartY = event.touches[0].clientY;
  isDragging.value = true;
}

function onTouchMove(event: TouchEvent) {
  if (!isDragging.value) return;
  dragOffset.value = Math.max(0, event.touches[0].clientY - dragStartY);
}

function onTouchEnd() {
  if (!isDragging.value) return;
  isDragging.value = false;
  const hasPulledEnough = dragOffset.value > DISMISS_DISTANCE;
  dragOffset.value = 0;
  if (hasPulledEnough) close();
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.preventDefault();
    close();
    return;
  }
  if (event.key !== 'Tab' || !panel.value) return;
  const focusable = [...panel.value.querySelectorAll<HTMLElement>(FOCUSABLE)];
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (!first) {
    event.preventDefault();
    panel.value.focus();
  } else if (!panel.value.contains(active) || (active === panel.value && !event.shiftKey)) {
    event.preventDefault();
    first.focus();
  } else if (event.shiftKey && (active === first || active === panel.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

function releaseFocus() {
  document.removeEventListener('keydown', onKeydown);
  if (opener?.isConnected) opener.focus();
  opener = null;
}

// Android back closes the sheet rather than leaving the page under it.
let unregisterBackHandler: (() => void) | undefined;

watch(
  open,
  isOpen => {
    unregisterBackHandler?.();
    unregisterBackHandler = undefined;
    if (!isOpen) return releaseFocus();
    opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.addEventListener('keydown', onKeydown);
    nextTick(() => panel.value?.focus({ preventScroll: true }));
    unregisterBackHandler = appStore.registerBackHandler(() => {
      close();
      return true;
    });
  },
  { immediate: true },
);

// Teleported to body, the sheet would otherwise outlive its kept-alive page (e.g. a push opens the alerts page); no leave animation over a page that is already gone.
const isDeactivated = ref(false);
onDeactivated(() => {
  isDeactivated.value = true;
  close();
});
onActivated(() => (isDeactivated.value = false));

onBeforeUnmount(() => {
  unregisterBackHandler?.();
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <transition name="modal-sheet" :css="!isDeactivated" :duration="TRANSITION_DURATION">
      <div v-if="open" class="modal-sheet">
        <div class="modal-sheet__backdrop" data-test="backdrop" @click="close" />
        <div
          ref="panel"
          aria-modal="true"
          class="modal-sheet__panel"
          :class="{ 'modal-sheet__panel--dragging': isDragging }"
          role="dialog"
          tabindex="-1"
          :aria-label="label"
          :style="{ transform: dragOffset ? `translateY(${dragOffset}px)` : undefined }"
        >
          <div
            class="modal-sheet__header"
            @touchstart.passive="onTouchStart"
            @touchmove.passive="onTouchMove"
            @touchend="onTouchEnd"
            @touchcancel="onTouchEnd"
          >
            <div class="modal-sheet__grabber" />
            <slot name="header" />
          </div>
          <div class="modal-sheet__content">
            <slot />
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style lang="scss">
.modal-sheet {
  inset: 0;
  position: fixed;
  z-index: 20;

  &__backdrop {
    background-color: rgba($color-neutral-highest, 0.4);
    inset: 0;
    position: absolute;
    transition: opacity 0.25s ease;
  }

  &__panel {
    background-color: $color-white;
    border-radius: 38px 38px 0 0;
    bottom: 0;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    left: 0;
    max-height: calc(100% - var(--status-bar-band, 0px) - $spacing-6);
    outline: none;
    position: absolute;
    right: 0;
    transition: transform 0.25s ease;

    &--dragging {
      transition: none;
    }
  }

  &__header {
    flex-shrink: 0;
    touch-action: none;
  }

  &__grabber {
    background-color: $color-neutral-medium;
    border-radius: 100px;
    height: 5px;
    margin: 8px auto 2px;
    width: 36px;
  }

  &__content {
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-bottom: calc($spacing-4 + env(safe-area-inset-bottom));
  }

  &-enter-from &__backdrop,
  &-leave-to &__backdrop {
    opacity: 0;
  }

  &-enter-from &__panel,
  &-leave-to &__panel {
    transform: translateY(100%);
  }
}
</style>
